/**
 * 65.49 step 2: the anti-dropship missiles in flight, drawn from the state every frame (state.missiles; rendering reads,
 * never writes). Each is the look's missile (rendering/antiDropshipMissileLooks.ts buildMissile, its team's palette),
 * posed by its age on the sim's own beats (the motor lights after the coast and burns to the end of its life), with its
 * smoke ribbon behind the nozzle while the motor burns. A missile gone (burst or spent) hides; its trail stays until its
 * last point fades, then both are freed. The look is in person units (1.8 a person), scaled to the world by the match's.
 */
import * as THREE from 'three';
import type { MissileFlight } from '../types';
import type { UnitPalette } from './unitPalette';
import { buildMissile, poseMissile, MissileTrail, TRAIL_LOOK, type MissileModel } from './antiDropshipMissileLooks';
import { disposeObjectTree } from './meshBake';
import { MISSILE_CLASSES, type MissileClass } from '../constants/weapons';

const PERSON_UNITS = 1.8;
/** Each class's beats (its motor lit after its coast, burning out its life) and its drawn size against the small's (69.50 step 2a: the site's medium round, a launcher's, larger; the large larger still). */
const BEATS = Object.fromEntries((Object.keys(MISSILE_CLASSES) as MissileClass[]).map(k => [k, { ignite: MISSILE_CLASSES[k].homing.coast, burn: MISSILE_CLASSES[k].homing.life - MISSILE_CLASSES[k].homing.coast }])) as Record<MissileClass, { ignite: number; burn: number }>;
const CLASS_SCALE: Record<MissileClass, number> = { small: 1, medium: 1.6, large: 2.4 };
const Z = new THREE.Vector3(0, 0, 1);

interface Flown { model: MissileModel; trail: MissileTrail; gone: boolean }

export class MissileFlights {
  private readonly flown = new Map<number, Flown>();
  private readonly dir = new THREE.Vector3();
  private readonly nozzle = new THREE.Vector3();

  constructor(private readonly scene: THREE.Scene) {}

  /** This frame: the live missiles (world units), the camera the ribbons face, the frame's clock (s), a person's height (world) and each team's palette. */
  update(live: readonly MissileFlight[], camera: THREE.Camera, time: number, person: number, paletteOf: (team: number) => UnitPalette): void {
    if (!live.length && !this.flown.size) return;
    const s0 = person / PERSON_UNITS;
    for (const f of this.flown.values()) f.gone = true;
    for (const m of live) {
      let f = this.flown.get(m.id);
      const cls = m.cls ?? 'small', s = s0 * CLASS_SCALE[cls], beats = BEATS[cls];
      if (!f) {
        const model = buildMissile(paletteOf(m.team));
        model.group.scale.setScalar(s);
        const trail = new MissileTrail({ ...TRAIL_LOOK, width: TRAIL_LOOK.width * s, spread: TRAIL_LOOK.spread * s });   // (its widths in person units)
        this.scene.add(model.group, trail.mesh);
        f = { model, trail, gone: false };
        this.flown.set(m.id, f);
      }
      f.gone = false;
      this.dir.set(m.vel.x, m.vel.y, m.vel.z).normalize();
      f.model.group.position.set(m.pos.x, m.pos.y, m.pos.z);
      f.model.group.quaternion.setFromUnitVectors(Z, this.dir);
      poseMissile(f.model, m.age, beats);
      if (m.age >= beats.ignite) f.trail.push(this.nozzle.copy(f.model.group.position).addScaledVector(this.dir, -(f.model.length / 2) * s), time);
    }
    for (const [id, f] of this.flown) {
      f.model.group.visible = !f.gone;
      f.trail.update(camera, time);
      if (f.gone && !f.trail.alive(time)) {
        this.scene.remove(f.model.group, f.trail.mesh);
        disposeObjectTree(f.model.group);
        f.trail.mesh.geometry.dispose(); (f.trail.mesh.material as THREE.Material).dispose();
        this.flown.delete(id);
      }
    }
  }

  dispose(): void {
    for (const f of this.flown.values()) { this.scene.remove(f.model.group, f.trail.mesh); disposeObjectTree(f.model.group); f.trail.mesh.geometry.dispose(); (f.trail.mesh.material as THREE.Material).dispose(); }
    this.flown.clear();
  }
}
